import { useMemo } from 'react';
import { getRulesetFeatures, getRulesetConfig } from '@character-creator/data';
import type { RulesetFeatures, RulesetConfig } from '@character-creator/data';
import { useCharacterCreationStore } from '../stores/characterCreationStore';

/**
 * Hook to get feature flags for the current ruleset
 * Use this to conditionally render UI based on ruleset capabilities
 *
 * @example
 * ```tsx
 * const features = useRulesetFeatures();
 * if (features.creation.hasTieredFeats) {
 *   return <TieredFeatSelector />;
 * }
 * ```
 */
export function useRulesetFeatures(): RulesetFeatures {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => getRulesetFeatures(rulesetId), [rulesetId]);
}

/**
 * Hook to get the full ruleset configuration
 */
export function useRulesetConfig(): RulesetConfig | undefined {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => getRulesetConfig(rulesetId), [rulesetId]);
}

/**
 * Hook to get UI terminology for the current ruleset
 * Use this to display ruleset-appropriate labels (e.g., "Species" vs "Race")
 *
 * @example
 * ```tsx
 * const terms = useRulesetTerminology();
 * return <h2>Choose Your {terms.race}</h2>;
 * ```
 */
export function useRulesetTerminology() {
  const features = useRulesetFeatures();
  return features.ui.terminology;
}
